import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Menu from "../main/Menu";
import { axiosClient } from "../../helpers/axios";
import Header from "../main/Header";
import { useSelector } from "react-redux";
import ChatBot from "../chatBot/ChatBot"; // O tu ChatIaButton si usas ese
import { clientsCompanyResource } from "../../helpers/configRoutes/backend_helper";
import usePlanUsage from "../../hooks/usePlanUsage";

const MiddlewareAuth = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const auth = useSelector((state) => state.Login.auth);
  const user = useSelector((state) => state.Login.user);
  const navigate = useNavigate();
  const token = ''; // Si luego lo tomas de Redux/LS, solo actualiza aquí
  const { getOverLimitFeatures, refresh: refreshUsage } = usePlanUsage();
  const fetchCompany = useCallback(async () => {
    if (!auth?.isAuth || !user?.company_id) {
      setCompany(null);
      return;
    }

    const response = await clientsCompanyResource("show", {}, user.company_id);
    if (response?.status && response?.company) {
      setCompany(response.company);
    }
  }, [auth?.isAuth, user?.company_id]);

  // Configurar interceptor solo una vez
  useEffect(() => {
    const interceptor = axiosClient.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Aquí podrías disparar logout global si quieres
        }
        return Promise.reject(error);
      }
    );

    // Simulación / hook para inicialización
    const timer = setTimeout(() => {
      setLoading(false);
    }, 400);

    return () => {
      clearTimeout(timer);
      axiosClient.interceptors.request.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    fetchCompany();

    const refreshCompany = async () => {
      await Promise.all([fetchCompany(), refreshUsage()]);
    };

    window.addEventListener("company-subscription-updated", refreshCompany);

    return () => {
      window.removeEventListener("company-subscription-updated", refreshCompany);
    };
  }, [fetchCompany, auth?.isAuth, user?.company_id, refreshUsage]);
  const trialBanner = company?.subscription?.banner;
  const overLimitFeatures = getOverLimitFeatures();

  // Pantalla de carga estilizada con tu branding
  if (loading) {
    return (
      <div className="w-screen h-screen flex flex-col items-center justify-center bg-cs-surface">
        <div className="mb-4 flex items-center gap-2">
          <img
            src="/logoB.png"
            alt="CargoSmart"
            className="h-8 w-auto object-contain"
          />
        </div>
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-cs-primary border-t-transparent" />
        <p className="mt-3 text-xs text-cs-muted tracking-wide">
          Loading your workspace...
        </p>
      </div>
    );
  }

  // Si no está autenticado → al login
  if (!auth || !auth.isAuth) {
    return <Navigate to="/login" />;
  }

  // Layout autenticado
  return (
    <div className="w-screen h-screen flex flex-col bg-cs-surface">
      {/* Top header */}
      <Header />

      {/* Body layout */}
      <div className="flex flex-row flex-1 overflow-hidden">
        {/* Sidebar */}
        <Menu />

        {/* Main content */}
        <main
          className="
            flex-1 h-full overflow-auto
            bg-cs-surface
            px-4 lg:px-6 pt-4 pb-28 lg:pb-32
          "
        >
          {trialBanner?.show && (
            <div className={`mb-4 rounded-2xl border px-4 py-3 shadow-sm ${trialBanner.is_expired
              ? "border-amber-300 bg-amber-50"
              : "border-cs-primary/20 bg-cs-primary-soft"
              }`}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-cs-primary-dark">
                    Trial version
                  </p>
                  <h3 className="text-sm font-semibold text-cs-dark mt-1">
                    {trialBanner.title}
                  </h3>
                  <p className="text-xs text-cs-muted mt-1 max-w-3xl">
                    {trialBanner.message}
                    {!trialBanner.is_expired && typeof trialBanner.days_remaining === "number" && (
                      <span className="font-medium text-cs-primary-dark"> {trialBanner.days_remaining} day{trialBanner.days_remaining !== 1 ? "s" : ""} remaining.</span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/subscriptions")}
                  className="inline-flex items-center justify-center rounded-xl bg-cs-primary px-3 py-2 text-xs font-medium text-white transition-all hover:bg-cs-primary-hover"
                >
                  {trialBanner.cta_label || "Buy full subscription"}
                </button>
              </div>
            </div>
          )}
          {overLimitFeatures.length > 0 && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-red-700">
                    Plan over limit
                  </p>
                  <h3 className="text-sm font-semibold text-cs-dark mt-1">
                    Your current plan is below your active usage.
                  </h3>
                  <p className="text-xs text-cs-muted mt-1 max-w-3xl">
                    You can keep existing records, but new users or trucks will stay blocked until you reduce usage or upgrade again.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {overLimitFeatures.map((feature) => (
                    <span
                      key={feature.feature_key}
                      className="rounded-full border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-700"
                    >
                      {feature.feature_key === "users" ? "Users" : feature.feature_key === "trucks" ? "Trucks" : feature.feature_key}: {feature.used}/{feature.limit}
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => navigate("/subscriptions")}
                    className="inline-flex items-center justify-center rounded-xl bg-red-600 px-3 py-2 text-xs font-medium text-white transition-all hover:bg-red-700"
                  >
                    Review plans
                  </button>
                </div>
              </div>
            </div>
          )}
          {children}
        </main>

        {/* Chat / IA helper (flotante / lateral) */}
        <div className=" xl:flex items-end pr-4 pb-4">
          <ChatBot />
          {/* Si usas otro:
            <ChatIaButton />
          */}
        </div>
      </div>
    </div>
  );
};

export default MiddlewareAuth;
